import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Scan, Sparkles, Info } from 'lucide-react';
import { RadiologyTest } from '../../types/api.types';
import { CreateRadiologyTestDto, UpdateRadiologyTestDto } from '../../api/radiologyTests.api';
import { Input } from '../common/Input';
import { Button } from '../common/Button';

interface RadiologyTestFormProps {
  radiologyTest?: RadiologyTest;
  onSubmit: (data: CreateRadiologyTestDto | UpdateRadiologyTestDto) => void;
  onCancel?: () => void;
  isLoading?: boolean;
}

export const RadiologyTestForm: React.FC<RadiologyTestFormProps> = ({
  radiologyTest,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateRadiologyTestDto>({
    defaultValues: radiologyTest
      ? {
          name: radiologyTest.name,
          description: radiologyTest.description || '',
        }
      : undefined,
  });

  return (
    <motion.form
      onSubmit={handleSubmit(onSubmit)}
      className="space-y-4"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Info Banner */}
      <motion.div
        className="d-flex align-items-start gap-3 p-3 rounded-3 mb-4"
        style={{
          background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)',
          border: '1px solid #7dd3fc',
        }}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.1 }}
      >
        <Info className="h-5 w-5 text-primary mt-1 flex-shrink-0" />
        <div>
          <p className="mb-0 fw-semibold text-primary" style={{ fontSize: '0.9rem' }}>
            {t('radiologyTests.quickAddHint', 'أدخل اسم فحص الأشعة لإضافته بسرعة. الوصف اختياري.')}
          </p>
        </div>
      </motion.div>

      {/* Radiology Test Name - Main Field */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <label className="d-flex align-items-center gap-2 mb-2 fw-semibold text-dark" style={{ fontSize: '0.95rem' }}>
          <Scan className="h-4 w-4 text-primary" />
          {t('radiologyTests.name', 'اسم فحص الأشعة')}
          <span className="text-danger">*</span>
        </label>
        <Input
          required
          error={errors.name?.message}
          autoFocus
          placeholder={t('radiologyTests.namePlaceholder', 'مثال: أشعة الصدر')}
          className="form-control-lg"
          style={{
            fontSize: '1rem',
            padding: '0.75rem 1rem',
            borderRadius: '0.5rem',
            border: '2px solid #e5e7eb',
            transition: 'all 0.3s',
          }}
          {...register('name', {
            required: t('radiologyTests.nameRequired', 'اسم فحص الأشعة مطلوب'),
            maxLength: {
              value: 200,
              message: t('radiologyTests.nameMaxLength', 'الاسم يجب ألا يتجاوز 200 حرف'),
            },
          })}
        />
      </motion.div>

      {/* Description */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <label className="d-flex align-items-center gap-2 mb-2 fw-semibold text-dark" style={{ fontSize: '0.95rem' }}>
          <Sparkles className="h-4 w-4 text-info" />
          {t('radiologyTests.description', 'الوصف')}
        </label>
        <Input
          error={errors.description?.message}
          placeholder={t('radiologyTests.descriptionPlaceholder', 'ملاحظات اختيارية')}
          style={{
            fontSize: '0.95rem',
            padding: '0.65rem 1rem',
            borderRadius: '0.5rem',
            border: '2px solid #e5e7eb',
          }}
          {...register('description', {
            maxLength: {
              value: 1000,
              message: t('radiologyTests.descriptionMaxLength', 'الوصف يجب ألا يتجاوز 1000 حرف'),
            },
          })}
        />
      </motion.div>

      {/* Action Buttons */}
      <motion.div
        className="d-flex gap-3 justify-content-end mt-4 pt-3 border-top"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
      >
        {onCancel && (
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={isLoading}
            className="px-4"
            style={{ fontWeight: 500 }}
          >
            {t('common.cancel')}
          </Button>
        )}
        <Button
          type="submit"
          isLoading={isLoading}
          variant="primary"
          className="px-4 d-flex align-items-center gap-2"
          style={{
            fontWeight: 600,
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            border: 'none',
            boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)',
          }}
        >
          <Scan className="h-4 w-4" />
          {radiologyTest ? t('common.save', 'Save') : t('common.add', 'Add')}
        </Button>
      </motion.div>
    </motion.form>
  );
};





